import { Box, Panel, Spinner } from '@garner-health/components-common';
import { useFlags } from '@garner-health/lib-ui-feature-flags';
import React, { useEffect, useState } from 'react';

import { ProviderResultItem } from '~/clients';
import { RateLimited } from '~/components/common';
import {
  DataSecurityDialog,
  NoResults,
  NotSupportedSearch,
  SearchFormData,
  SearchResults,
  StartSearch,
} from '~/components/provider-search';
import { SearchForm } from '~/components/provider-search/search-form';
import { useScopeInfo } from '~/contexts';
import { useRumLoadVital } from '~/hooks';
import { useSearchFormData } from '~/hooks/use-search-form-data';
import { isTerminalResultType, SearchResultType, useSearchFormResults } from '~/hooks/use-search-form-results';

function isTieredNetwork(networkId: string | undefined): boolean {
  if (!networkId) return false;
  return /^.*-tier\d+$/.test(networkId);
}

const MainContent = ({
  formData,
  providers,
  resultType,
  loadingSearch,
}: {
  formData: SearchFormData | null;
  providers: ProviderResultItem[] | null;
  resultType: SearchResultType | null;
  loadingSearch: boolean;
}) => {
  if (!formData) return <StartSearch />;
  //TODO: make types stricter so we know searchResults has a value when !loadingSearch
  if (!providers || loadingSearch) return <Spinner variant="dark" />;
  // Must precede the empty-results check below: a throttled search returns no providers, and
  // `NoResults` would tell the user to try another search when they should simply wait.
  if (resultType === 'rate_limited') return <RateLimited />;
  if (resultType === 'not_supported') return <NotSupportedSearch />;

  const tieredNetwork = isTieredNetwork(formData.network.value);
  if (!providers.length || !resultType) return <NoResults isTieredNetwork={tieredNetwork} />;
  return (
    <SearchResults
      providers={providers}
      formData={formData}
      responseSetType={resultType}
      isTieredNetwork={tieredNetwork}
    />
  );
};

const LoadingPage = () => (
  <Panel>
    <Panel.Header>
      <Box padding="sm" />
    </Panel.Header>
    <Panel.Main>
      <Spinner variant="dark" />
    </Panel.Main>
  </Panel>
);

export const SearchPage = () => {
  const { data: formData, loading: loadingFormData, onNewFormData, shouldReset } = useSearchFormData();
  const { showAdditionalProviders } = useFlags();
  const { data: searchResults, loading: loadingSearch } = useSearchFormResults({
    formData,
    showAdditionalProviders,
  });
  const [hasLoadedPage, setHasLoadedPage] = useState(false);
  const plan = useScopeInfo()?.plan;

  useRumLoadVital('search_page_loaded', loadingFormData);

  const providers = searchResults?.providers ?? null;
  const searchResultType = searchResults?.resultType ?? null;
  const showHcscOverride = plan === 'hcsc_compliant' && formData?.network.isHcscNetwork;
  // A terminal result (e.g. a throttled search) always wins over display overrides, or an HCSC
  // user would fall through to the empty-results state and be told to try another search.
  const resultType = showHcscOverride && !isTerminalResultType(searchResultType) ? 'hcsc_compliant' : searchResultType;

  useEffect(() => {
    if (!loadingFormData) {
      // once we've loaded the form once, we've loaded the page
      setHasLoadedPage(true);
    }
  }, [loadingFormData]);

  if (!hasLoadedPage) {
    return <LoadingPage />;
  }

  return (
    <>
      <Panel containScroll>
        <Panel.Header divider paddingTop="xs">
          <SearchForm
            initialValues={formData}
            onSubmit={onNewFormData}
            isDisabled={loadingSearch}
            shouldReset={shouldReset}
          />
        </Panel.Header>

        <Panel.Main paddingBottom="none" paddingTop="none">
          <MainContent
            formData={formData}
            providers={providers}
            resultType={resultType}
            loadingSearch={loadingSearch}
          />
        </Panel.Main>
      </Panel>
      <DataSecurityDialog />
    </>
  );
};
